import { SaveIcon } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { usePlayground } from "@/components/Playground/playground-store";
import SaveEntityToLocation from "@/components/SaveEntityToLocation";
import { Button } from "@/components/ui/button";
import { ToastType } from "@/enums";
import { EntityType } from "@/queries/folder-entities/types";
import { useCreateToolRegistry } from "@/queries/tool-registry";
import type { CustomFunction_, FunctionTool, RegistryTool } from "@/types";
import { displayToast } from "@/utils/toast";

interface SaveToRegistryPopoverProps {
  functionDef: CustomFunction_;
  onReplace: (registryTool: RegistryTool) => void;
}

const SaveToRegistryPopover = ({ functionDef, onReplace }: SaveToRegistryPopoverProps) => {
  const { workspaceId } = useParams();
  const workspaceIdNum = workspaceId ? Number(workspaceId) : null;
  const playground = usePlayground();
  const [open, setOpen] = useState(false);

  const toolDefinition: FunctionTool = {
    type: "function",
    function: {
      name: functionDef.name,
      description: functionDef.description ?? "",
      parameters: functionDef.parameters ?? {},
      strict: functionDef.strict ?? false,
    },
  };

  const createMutation = useCreateToolRegistry({
    onSuccess: data => {
      // Register the resolved definition for playground execution
      playground.registerToolDefinition(data.tool_registry.id, toolDefinition);

      // Replace the inline tool with a registry reference
      const registryTool: RegistryTool = {
        type: "registry",
        tool_registry_id: data.tool_registry.id,
        label: null,
        version_number: null,
        _resolved_name: functionDef.name,
      };

      onReplace(registryTool);
      setOpen(false);
      displayToast(
        "Tool saved to registry. Save the prompt template to keep this change.",
        ToastType.warning,
      );
    },
  });

  const handleSave = async (name: string, folderId: number | null) => {
    if (!workspaceIdNum) {
      return;
    }

    try {
      await createMutation.mutateAsync({
        workspaceId: workspaceIdNum,
        name,
        folderId,
        toolDefinition: toolDefinition as unknown as Record<string, unknown>,
        commitMessage: "Saved from inline tool",
      });
    } catch {
      // Error toast is handled by the mutation's onError — swallow here
      // to prevent SaveEntityToLocation from showing a duplicate toast
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="xs"
        className="h-6 gap-1.5 px-2"
        onClick={e => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <SaveIcon className="h-3 w-3" />
        <span className="text-[11px]">Save as...</span>
      </Button>

      <SaveEntityToLocation
        type={EntityType.TOOL}
        onSave={handleSave}
        currentFolderId={null}
        defaultName={functionDef.name}
        open={open}
        onOpenChange={setOpen}
        title="Save Tool to Registry"
        description="Save this tool definition to your workspace registry for reuse across prompts."
        saveButtonText="Save"
      />
    </>
  );
};

export default SaveToRegistryPopover;
